import readline from 'node:readline/promises'

import { PromptAbortedError, SelectionAbortedError } from '../../errors'
import type { Ask, AskRequest } from '../../operations/init/answers'
import { TRUTHY } from '../../operations/init/answers'
import type { Prompt } from '../../templates/manifest'
import type { Template } from '../../templates/manifest'
import { markDirty, say } from '../output'
import { canRenderList, selectFromList } from './select-list'

// The half of prompting that needs a person: reading keystrokes, redrawing a list, asking again when an
// answer does not validate. `operations/init/answers` decides *what* still needs an answer; this decides
// how it gets one.
//
// Built on node's own readline — a scaffolding command is not worth a dependency, and the CLI ships to end
// users who install it into their own projects.

// Dimming is cosmetic, so it is dropped when stdout is not a terminal — piping a scaffold's output into
// a log should not sprinkle escape codes through it.
const colored = Boolean(process.stdout.isTTY) && !process.env.NO_COLOR
const DIM = colored ? '\x1b[2m' : ''
const RESET = colored ? '\x1b[0m' : ''

function hint(text: string | undefined): void {
  if (text) {
    say(`${ DIM }  ${ text }${ RESET }`)
  }
}

export interface Prompter {
  ask: Ask
  /** Safe to call whether or not anything was ever asked. */
  close: () => void
}

/**
 * A prompting session, holding one readline interface across every question.
 *
 * Opened lazily: a run where every answer came from a flag should not touch stdin at all, and an interface
 * created and never used still has to be closed or it holds the process open.
 */
export function createPrompter(): Prompter {
  let rl: readline.Interface | null = null

  const readlineInterface = () => (rl ??= readline.createInterface({
    input : process.stdin,
    output: process.stdout,
  }))

  return {
    ask  : request => askOne(request, readlineInterface),
    close: () => rl?.close(),
  }
}

async function askOne(
  { prompt, fallback, validate }: AskRequest,
  readlineInterface: () => readline.Interface
): Promise<string | boolean> {
  // readline writes the question and echoes the answer itself, so the shared tracker cannot see any of it.
  markDirty()
  hint(prompt.hint)

  if (prompt.type === 'choice') {
    return askChoice(prompt, String(fallback), readlineInterface)
  }

  if (prompt.type === 'boolean') {
    const suffix = fallback ? 'Y/n' : 'y/N'
    const raw = (await readlineInterface().question(`${ prompt.label } (${ suffix }): `)).trim().toLowerCase()

    return raw === '' ? Boolean(fallback) : TRUTHY.includes(raw)
  }

  while (true) {
    const raw = (await readlineInterface().question(`${ prompt.label } (${ fallback }): `)).trim()
    const value = raw === '' ? String(fallback) : raw
    const failure = validate(value)

    if (!failure) {
      return value
    }

    say(`  ${ failure }`)
  }
}

/**
 * A choice prompt: the arrow-key list where the terminal allows it, a numbered list otherwise.
 *
 * Cancelling the list falls back to keeping the default rather than aborting the whole command — the user
 * is midway through answering, and losing the earlier answers to an accidental Escape would be rude.
 */
async function askChoice(
  prompt: Extract<Prompt, { type: 'choice' }>,
  fallback: string,
  readlineInterface: () => readline.Interface
): Promise<string> {
  const startAt = Math.max(0, prompt.options.findIndex(option => option.value === fallback))

  if (canRenderList()) {
    try {
      const chosen = await selectFromList(`${ prompt.label }:`, prompt.options.map(option => ({
        label      : option.label,
        description: option.description,
      })), startAt)

      return prompt.options[chosen].value
    } catch (error) {
      if (error instanceof SelectionAbortedError) {
        return fallback
      }

      throw error
    }
  }

  for (const [index, option] of prompt.options.entries()) {
    say(`  ${ index + 1 }) ${ option.label }${ DIM }${ option.description ? ` — ${ option.description }` : '' }${ RESET }`)
  }

  while (true) {
    const raw = (await readlineInterface().question(`${ prompt.label } (${ fallback }): `)).trim()

    if (raw === '') {
      return fallback
    }

    const byNumber = prompt.options[Number(raw) - 1]
    const byValue = prompt.options.find(option => option.value === raw)

    if (byNumber || byValue) {
      return (byNumber ?? byValue)!.value
    }

    say(`  Not an option. Enter 1-${ prompt.options.length } or one of: ` +
      prompt.options.map(option => option.value).join(', '))
  }
}

/** Fallback for a terminal that cannot do raw-mode keypresses: type a number or an id. */
async function selectTemplateByTyping(templates: Template[]): Promise<Template> {
  say('\nAvailable templates:\n')

  for (const [index, { manifest, isDefault }] of templates.entries()) {
    const marker = isDefault ? ' (default)' : ''

    say(`  ${ index + 1 }) ${ manifest.label }${ marker } ${ DIM }(${ manifest.id })${ RESET }`)
    say(`     ${ DIM }${ manifest.description }${ RESET }`)
  }

  say('')

  const rl = readline.createInterface({ input: process.stdin, output: process.stdout })

  try {
    while (true) {
      const answer = (await rl.question(`Template (1-${ templates.length }): `)).trim()
      const byNumber = templates[Number(answer) - 1]
      const byId = templates.find(template => template.manifest.id === answer)
      const chosen = byNumber ?? byId

      if (chosen) {
        return chosen
      }

      say(`  Not a template. Enter a number 1-${ templates.length } or a template id.`)
    }
  } finally {
    rl.close()
  }
}

/**
 * Interactive selection of a template — arrow keys where the terminal supports it, typed input where it
 * does not. The default template is listed first, so Enter alone picks it.
 */
export async function selectTemplate(templates: Template[]): Promise<Template> {
  if (!canRenderList()) {
    return selectTemplateByTyping(templates)
  }

  const choices = templates.map(({ manifest, isDefault }) => ({
    label      : `${ manifest.label }${ isDefault ? ' (default)' : '' }`,
    description: manifest.description,
  }))

  try {
    return templates[await selectFromList('Choose a template:', choices)]
  } catch (error) {
    if (error instanceof SelectionAbortedError) {
      throw new PromptAbortedError('Cancelled — no service was created.')
    }

    throw error
  }
}
